import { useCallback, useRef, useState } from "react";
import { weekdayIntervals } from "@/features/availability/utils/businessHours";
import { TIMELINE_PX_PER_MINUTE } from "@/features/calendar-events/utils/dayTimeline";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { calendarDate } from "@/shared/utils/calendarDate";
import { formatBusinessDate, getBusinessDateKey } from "@/shared/utils/formatters";
import { AgendaDayStream, type AgendaDayStreamHandle } from "../components/AgendaDayStream";
import { AgendaWeekStrip } from "../components/AgendaWeekStrip";
import { DashboardHeader } from "../components/DashboardHeader";
import { useAgendaActiveDay } from "../hooks/useAgendaActiveDay";
import { useAgendaRange } from "../hooks/useAgendaRange";
import { useDashboardContext } from "../hooks/useDashboardContext";
import { createAgendaDays } from "../utils/agendaDays";

const DAY_CHROME_PX = 72;
const CLOSED_DAY_PX = 96;

const minutes = (time: string) => {
  const [hours, mins] = time.split(":").map(Number);
  return hours * 60 + mins;
};

export function AgendaPage() {
  const { user, businessId, locationId, business, location } = useDashboardContext();

  if (!user || !business || !location || location.businessId !== businessId) {
    return (
      <Page>
        <DashboardHeader view="agenda" />
        <PageContent>
          <h1 className="sr-only">Agenda</h1>
          {!user ? <RowSkeleton /> : <EmptyState message="This business location is unavailable." />}
        </PageContent>
      </Page>
    );
  }

  return <AgendaCalendar key={`${businessId}:${locationId}`} userId={user.id} />;
}

function AgendaCalendar({ userId }: { userId: string }) {
  const { businessId, locationId, business, now } = useDashboardContext();
  const { goToAppointmentDetails, goToAppointmentAdd, goToCalendarEventDetails } = useAppNavigation();
  const timezone = business?.timezone ?? "UTC";
  const todayKey = getBusinessDateKey(now, timezone);
  const [days] = useState(() => createAgendaDays(todayKey));
  const { activeKey: requestedKey, selectDay } = useAgendaActiveDay(todayKey);
  const activeKey = days.dayIndexOf(requestedKey) === null ? todayKey : requestedKey;
  const [visible, setVisible] = useState(() => {
    const index = days.dayIndexOf(activeKey) ?? 0;
    return { first: Math.max(0, index - 1), last: Math.min(days.dayCount - 1, index + 3) };
  });
  const stream = useRef<AgendaDayStreamHandle>(null);

  const range = useAgendaRange({ days, firstDay: visible.first, lastDay: visible.last, userId, businessId, locationId, timezone, now });
  const schedule = range.schedule;

  const intervals = useCallback((dateKey: string) => schedule ? weekdayIntervals(schedule.schedule, dateKey) : [], [schedule]);
  const isClosedDay = (dateKey: string) => !!schedule && intervals(dateKey).length === 0;
  // Stable per schedule: a new estimate shifts every unmeasured offset under the current scroll position.
  const estimateDayHeight = useCallback((dateKey: string) => {
    const hours = intervals(dateKey);
    if (schedule && !hours.length) return CLOSED_DAY_PX;
    const start = hours.length ? Math.min(...hours.map((interval) => minutes(interval.start))) : 9 * 60;
    const end = hours.length ? Math.max(...hours.map((interval) => minutes(interval.end))) : 17 * 60;
    return (end - start + 60) * TIMELINE_PX_PER_MINUTE + DAY_CHROME_PX;
  }, [intervals, schedule]);

  const onVisibleRangeChange = useCallback((first: number, last: number) => {
    setVisible((current) => current.first === first && current.last === last ? current : { first, last });
  }, []);
  const jumpToDay = (dateKey: string) => {
    selectDay(dateKey);
    stream.current?.scrollToDay(dateKey);
  };
  const active = calendarDate(activeKey);

  return (
    <Page>
      <DashboardHeader view="agenda" />
      <h1 className="sr-only">Agenda</h1>
      <div className="mx-auto flex w-full max-w-[960px] shrink-0 items-center justify-between gap-3 px-4 pt-2">
        <p className="text-sm font-bold" aria-live="polite">
          {active.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
          <span className="ml-2 font-normal text-muted-foreground">{active.toLocaleDateString(undefined, { weekday: "long", day: "numeric" })}</span>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={activeKey === todayKey}
          onClick={() => jumpToDay(todayKey)}
        >
          Today
        </Button>
      </div>
      <AgendaWeekStrip
        days={days}
        activeKey={activeKey}
        todayKey={todayKey}
        isClosedDay={isClosedDay}
        onDaySelect={jumpToDay}
      />
      {!range.scheduleReady ? (
        <PageContent>
          <RowSkeleton />
        </PageContent>
      ) : (
        <AgendaDayStream
          ref={stream}
          days={days}
          initialKey={activeKey}
          todayKey={todayKey}
          timezone={timezone}
          now={now}
          dayData={range.dayData}
          onRetry={range.retry}
          estimateDayHeight={estimateDayHeight}
          onActiveDayChange={selectDay}
          onVisibleRangeChange={onVisibleRangeChange}
          onAppointmentClick={(selected) => goToAppointmentDetails(selected.id, false)}
          onEventClick={(event) => goToCalendarEventDetails(event.id)}
          onGapAdd={(startAt) => goToAppointmentAdd(undefined, { date: getBusinessDateKey(startAt, timezone), time: formatBusinessDate(startAt, timezone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }), startAt: startAt.toISOString() })}
        />
      )}
    </Page>
  );
}
